/**
 * Creative Studio · Step 2 — Creative Brief strip + 3 direction cards.
 * Each card renders an actual visual preview using the direction's spec.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, CheckCircle } from '@phosphor-icons/react';
import { CreativePreview, RENDER_W } from './CreativePreview';

const LIME = '#c8f060';

const BriefStrip = ({ brief, inheritedStrategy }) => (
  <div className="rounded-3xl p-6 mb-7"
    style={{ background: 'linear-gradient(180deg, #131313, #0c0c0c)', border: '1px solid rgba(255,255,255,0.06)' }}
    data-testid="creative-brief-strip">
    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
      <div>
        <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-1.5" style={{ color: LIME }}>Creative brief</p>
        <h2 className="text-2xl" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', fontWeight: 500 }}>
          {brief.what_promoting}
        </h2>
      </div>
      {inheritedStrategy && (
        <span className="px-3 py-1 rounded-full text-[10px] font-semibold inline-flex items-center gap-1.5"
          style={{ background: 'rgba(200,240,96,0.08)', color: LIME, border: '1px solid rgba(200,240,96,0.2)' }}>
          <Briefcase size={11} /> {inheritedStrategy.campaign_name}
        </span>
      )}
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      {[
        ['Campaign goal', (brief.campaign_goal || '').replace(/_/g, ' ')],
        ['Emotional trigger', (brief.emotional_trigger || '').replace(/_/g, ' ')],
        ['Tone', brief.tone],
        ['Industry', brief.industry],
      ].map(([k, v]) => (
        <div key={k}>
          <p className="text-[9px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>{k}</p>
          <p className="text-sm capitalize font-semibold" style={{ color: '#fff' }}>{v || '—'}</p>
        </div>
      ))}
    </div>
    <div className="pt-4 mt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <p className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1.5" style={{ color: 'rgba(245,240,232,0.5)' }}>Strategic insight</p>
      <p className="text-base italic leading-relaxed" style={{ color: 'rgba(245,240,232,0.85)', fontFamily: "'Instrument Serif', serif" }}>
        &ldquo;{brief.key_insight}&rdquo;
      </p>
    </div>
  </div>
);

const DIRECTION_ORDER = ['luxury_editorial', 'modern_commercial', 'conversion_focused'];

const DirectionCard = ({ direction, prompt, onPick, generating, selectedId }) => {
  const isSel = selectedId === direction.id;
  const letter = String.fromCharCode(65 + (DIRECTION_ORDER.indexOf(direction.id) >= 0 ? DIRECTION_ORDER.indexOf(direction.id) : 0));
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-3xl overflow-hidden cursor-pointer transition-all"
      data-testid={`direction-card-${direction.id}`}
      style={{
        background: '#0e0e0e',
        border: `1px solid ${isSel ? LIME : 'rgba(255,255,255,0.07)'}`,
        boxShadow: isSel ? '0 0 0 3px rgba(200,240,96,0.15)' : '0 8px 30px -16px rgba(0,0,0,0.6)',
      }}
      onClick={() => !generating && onPick(direction.id)}
    >
      <div className="aspect-square w-full overflow-hidden relative" style={{ background: direction.visual_spec?.background || '#0a0a0a' }}>
        <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: RENDER_W * 2, height: RENDER_W * 2 }}>
          <CreativePreview surface="flyer" direction={direction} copy={null} placeholderText={prompt} />
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: LIME }}>Direction {letter}</p>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "'Instrument Serif', serif", color: '#fff' }}>{direction.name}</h3>
          </div>
          {isSel && <CheckCircle size={20} weight="fill" style={{ color: LIME }} />}
        </div>
        <p className="text-[11px] mb-3" style={{ color: 'rgba(245,240,232,0.5)' }}>
          Feels like {(direction.feels_like || []).join(' · ')}
        </p>
        <p className="text-[12px] mb-3 leading-relaxed" style={{ color: 'rgba(245,240,232,0.7)' }}>{direction.rationale}</p>
        <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <span className="text-[10px] tracking-[0.15em] uppercase" style={{ color: 'rgba(245,240,232,0.4)' }}>{direction.best_for}</span>
          <button
            onClick={(e) => { e.stopPropagation(); onPick(direction.id); }}
            disabled={generating}
            data-testid={`direction-pick-${direction.id}`}
            className="px-3 py-1.5 rounded-full text-[11px] font-bold transition"
            style={{
              background: isSel ? LIME : 'rgba(200,240,96,0.1)',
              color: isSel ? '#0a0a0a' : LIME,
              border: '1px solid ' + (isSel ? LIME : 'rgba(200,240,96,0.2)'),
            }}>
            {generating && isSel ? 'Creating…' : isSel ? 'Selected' : 'Pick this'} →
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default function DirectionsStep({ brief, directions, inheritedStrategy, prompt, onPick, generating, selectedId, onReset }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="creative-directions-step">
      <div className="flex items-center justify-between mb-6">
        <p className="text-[12px]" style={{ color: 'rgba(245,240,232,0.45)' }}>Pick a direction. The system creates the campaign.</p>
        <button onClick={onReset} className="text-[12px] font-semibold" style={{ color: 'rgba(245,240,232,0.45)' }}>
          Start over
        </button>
      </div>
      <BriefStrip brief={brief} inheritedStrategy={inheritedStrategy} />
      <div className="grid lg:grid-cols-3 gap-5">
        {directions.map((d) => (
          <DirectionCard key={d.id} direction={d} prompt={prompt} onPick={onPick} generating={generating} selectedId={selectedId} />
        ))}
      </div>
    </motion.div>
  );
}
